import React, { FC, useEffect, useState, useRef } from 'react';
import { SearchInput } from 'shared/ui/SearchInput';
import { TTheme } from 'widgets/choose-background/ui/types';
import { useInView } from 'react-intersection-observer';
import { useStockImages } from 'features/list-stock-images';
import { Loader } from 'shared/ui';
import { cn } from 'shared/lib';
import Modal from 'components/shared/Modal';
import { IoMdClose } from 'react-icons/io';

type ImageSource =
  | { type: 'custom'; url: string }
  | { type: 'stock'; url: string; thumb: string };

interface IProps {
  theme: TTheme;
  onChange: (value: string) => void;
  customImageBackgrounds: string[];
  setCustomImageBackgrounds: (backgrounds: string[]) => void;
  selectedBackground: string;
}

export const ImagesTab: FC<IProps> = ({
  theme,
  selectedBackground,
  customImageBackgrounds,
  onChange
}) => {
  const {
    images,
    changeQuery,
    changePage,
    loaded: isImageLoaded
  } = useStockImages();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [root, setRoot] = useState<Element | null>(null);
  const [open, setOpen] = useState(false);

  const { ref: sentinelRef, inView } = useInView({
    root,
    threshold: 0.5
  });

  useEffect(() => {
    setRoot(scrollRef.current);
  }, []);

  useEffect(() => {
    if (inView) changePage();
  }, [inView]);

  const handleSearch = (value: string) => {
    changeQuery(value || 'video background');
  };

  // @ts-ignore
  const combinedImages: ImageSource[] = [
    // ...customImageBackgrounds.map(
    //   (url): ImageSource => ({ type: 'custom', url })
    // ),
    ...images.map((image) => ({
      type: 'stock',
      url: image.regular,
      thumb: image.thumb
    }))
  ];

  return (
    <>
      <div className="ag-flex ag-flex-row ag-justify-between ag-items-center ag-text-sm">
        <p className="ag-font-bold">Images</p>
        <button
          onClick={() => setOpen(true)}
          type="button"
          className="ag-text-vivid-purple"
        >
          See all
        </button>
      </div>

      <div className="ag-grid ag-grid-cols-3 ag-gap-2">
        {selectedBackground &&
          selectedBackground.includes('images.unsplash.com') && (
            <div
              style={{
                backgroundImage: `url(${selectedBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
              className={cn(
                'ag-w-full ag-relative ag-h-custom-72 ag-aspect-square ag-rounded-lg ag-border ag-cursor-pointer',
                selectedBackground
                  ? 'ag-border-[3px] ag-border-primary-600'
                  : theme === 'dark'
                  ? 'ag-border-neutral-700'
                  : 'ag-border-neutral-200'
              )}
            >
              <button
                onClick={() => onChange('')}
                className="ag-absolute ag-top-2 ag-right-2 ag-bg-white ag-text-black ag-rounded-full ag-w-6 ag-h-6 ag-flex ag-items-center ag-justify-center ag-shadow-sm hover:ag-bg-gray-100"
                title="Remove selected background"
              >
                <IoMdClose size={18} color="#000" />
              </button>
            </div>
          )}

        {combinedImages.slice(0, 9).map((image) => {
          const value = image.url;
          const isSelected = selectedBackground === value;

          return (
            <div
              key={image.url}
              className={cn(
                'ag-w-full ag-h-custom-72 ag-aspect-square ag-rounded-lg ag-border ag-cursor-pointer',
                isSelected
                  ? 'ag-border-[3px] ag-border-primary-600'
                  : theme === 'dark'
                  ? 'ag-border-neutral-700'
                  : 'ag-border-neutral-200'
              )}
              onClick={() => onChange(value)}
              style={{
                background: `url(${
                  image.type === 'custom' ? image.url : image.thumb
                })`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
              }}
            />
          );
        })}
      </div>

      <Modal
        setOpen={setOpen}
        open={open}
        noPadding
        positionClasses="ag-inset-0"
        rootClassName="!ag-z-[60]"
        className="ag-relative ag-transform ag-rounded-lg ag-text-left ag-shadow-xl ag-transition-all ag-w-[62rem]"
      >
        <div className="ag-p-6 ag-flex ag-flex-col md:h-[45rem]">
          <div className="ag-mb-4">
            <SearchInput onChange={handleSearch} theme={theme} />
          </div>
          <div
            ref={scrollRef}
            className="ag-flex-1 ag-overflow-y-auto"
            style={{ overflowY: 'auto', paddingRight: '0.25rem' }}
          >
            <div className="ag-grid ag-grid-cols-4 ag-gap-3 ag-mt-4">
              {images.map((image, index) => {
                const value = image.regular;
                const isSelected = selectedBackground === value;
                const isLast = index === images.length - 1;

                return (
                  <div
                    key={`${image.id}-${index}`}
                    ref={isLast ? sentinelRef : null}
                    className={cn(
                      'ag-w-full ag-h-custom-72 ag-aspect-square ag-rounded-lg ag-border ag-cursor-pointer',
                      isSelected
                        ? 'ag-border-[3px] ag-border-primary-600'
                        : theme === 'dark'
                        ? 'ag-border-neutral-700'
                        : 'ag-border-neutral-200'
                    )}
                    onClick={() => onChange(value)}
                    style={{
                      background: `url(${image.thumb})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover'
                    }}
                  />
                );
              })}
              {!isImageLoaded && (
                <div className="ag-col-span-full ag-flex ag-justify-center ag-my-4">
                  <Loader size={50} />
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
